import fetchRetry from 'fetch-retry';

// Wrap fetch with automatic retry + exponential backoff for Supabase REST calls.
// Covers all fire-and-forget upserts, persistMessage, analytics inserts.
// WebSocket (Realtime) connections are unaffected — they don't use fetch.
const fetchWithRetry = fetchRetry((...args) => fetch(...args), {
  retries: 3,
  retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 16000), // 2s, 4s, 8s
  retryOn: function (attempt, error, response) {
    // Retry on network errors (TypeError: Load failed / Failed to fetch)
    if (error !== null) return true;
    // Retry on transient server errors
    if (response && [408, 503, 504].includes(response.status)) return true;
    return false;
  },
});

// Nearby (proximity) calls skip the retry (signup-proximity-reliability, Mark
// 2026-10-13): a check-in repeats every ~1 s, so the next one IS the retry —
// the 2 s / 4 s waits held a woken phone off the list for ~10 s — and a goodbye
// retried late could erase a phone that had already come back.
// Sign-in calls skip it too (signup-cant-connect, Mark 2026-10-14): sign-in
// already retries itself (1 s, 3 s; a refresh backs off on its own), and
// stacking the 2 s / 4 s / 8 s waits on top held the splash ~45 s offline
// before the can't-connect screen could show. Anonymous sign-in is /signup;
// renewing a stored sign-in is /token.
// The both-tap offer calls skip it too (055 review, 12.12): each is let go at
// 2 s and the next ~1 s poll is the retry — a call held in the 2 s / 4 s waits
// never gave up, and the phone stopped polling.
const NO_RETRY = /\/rest\/v1\/rpc\/(proximity|offer)_|\/auth\/v1\/(signup|token)\b/;

/** The Supabase client's fetch: retried, except nearby, offer and sign-in calls. */
export const supabaseFetch = (input, init) => {
  const url = typeof input === 'object' && input?.url ? input.url : String(input);
  return NO_RETRY.test(url) ? fetch(input, init) : fetchWithRetry(input, init);
};
